/**
 * Contacts / People types.
 * Mirrors the mobile app's people collection at users/{uid}/people/{personId}.
 */

// ─── Enums & Option Arrays ─────────────────────────────────────────────────

export type RelationshipType =
  | 'friend' | 'bestFriend' | 'closeFriend' | 'familyFriend'
  | 'partner' | 'exPartner' | 'crush' | 'datePartner'
  | 'spouse' | 'fiance' | 'boyfriend' | 'girlfriend'
  | 'parent' | 'mother' | 'father' | 'stepParent'
  | 'child' | 'son' | 'daughter' | 'stepChild'
  | 'sibling' | 'brother' | 'sister' | 'halfSibling' | 'stepSibling'
  | 'grandparent' | 'grandchild'
  | 'uncle' | 'aunt' | 'cousin' | 'nephew' | 'niece'
  | 'inLaw' | 'parentInLaw' | 'siblingInLaw' | 'childInLaw'
  | 'colleague' | 'boss' | 'employee' | 'mentor' | 'mentee'
  | 'classmate' | 'teacher' | 'student'
  | 'neighbour' | 'acquaintance' | 'other';

export type IntentType =
  | 'catchUp' | 'deepen' | 'maintain' | 'celebrate'
  | 'support' | 'collaborate' | 'reconnect' | 'apologise'
  | 'thankYou' | 'checkIn' | 'planEvent' | 'shareNews'
  | 'askAdvice' | 'giveAdvice' | 'introduce' | 'networkWith'
  | 'justBecause' | 'other';

export type FrequencyLevel = 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly';

export type MeetingContext =
  | 'school' | 'work' | 'online' | 'event'
  | 'throughFriends' | 'throughFamily' | 'neighbourhood' | 'travel'
  | 'dating' | 'gym' | 'hobby' | 'volunteering'
  | 'professional' | 'socialMedia' | 'randomEncounter' | 'other';

export const RELATIONSHIP_OPTIONS: { value: RelationshipType; label: string; group: string }[] = [
  // Friends
  { value: 'friend', label: 'Friend', group: 'Friends' },
  { value: 'bestFriend', label: 'Best Friend', group: 'Friends' },
  { value: 'closeFriend', label: 'Close Friend', group: 'Friends' },
  { value: 'familyFriend', label: 'Family Friend', group: 'Friends' },
  // Romantic
  { value: 'partner', label: 'Partner', group: 'Romantic' },
  { value: 'spouse', label: 'Spouse', group: 'Romantic' },
  { value: 'fiance', label: 'Fiancé', group: 'Romantic' },
  { value: 'boyfriend', label: 'Boyfriend', group: 'Romantic' },
  { value: 'girlfriend', label: 'Girlfriend', group: 'Romantic' },
  { value: 'crush', label: 'Crush', group: 'Romantic' },
  { value: 'datePartner', label: 'Date Partner', group: 'Romantic' },
  { value: 'exPartner', label: 'Ex-Partner', group: 'Romantic' },
  // Family
  { value: 'parent', label: 'Parent', group: 'Family' },
  { value: 'mother', label: 'Mother', group: 'Family' },
  { value: 'father', label: 'Father', group: 'Family' },
  { value: 'stepParent', label: 'Step-Parent', group: 'Family' },
  { value: 'child', label: 'Child', group: 'Family' },
  { value: 'son', label: 'Son', group: 'Family' },
  { value: 'daughter', label: 'Daughter', group: 'Family' },
  { value: 'stepChild', label: 'Step-Child', group: 'Family' },
  { value: 'sibling', label: 'Sibling', group: 'Family' },
  { value: 'brother', label: 'Brother', group: 'Family' },
  { value: 'sister', label: 'Sister', group: 'Family' },
  { value: 'halfSibling', label: 'Half-Sibling', group: 'Family' },
  { value: 'stepSibling', label: 'Step-Sibling', group: 'Family' },
  { value: 'grandparent', label: 'Grandparent', group: 'Family' },
  { value: 'grandchild', label: 'Grandchild', group: 'Family' },
  { value: 'uncle', label: 'Uncle', group: 'Family' },
  { value: 'aunt', label: 'Aunt', group: 'Family' },
  { value: 'cousin', label: 'Cousin', group: 'Family' },
  { value: 'nephew', label: 'Nephew', group: 'Family' },
  { value: 'niece', label: 'Niece', group: 'Family' },
  { value: 'inLaw', label: 'In-Law', group: 'Family' },
  { value: 'parentInLaw', label: 'Parent-in-Law', group: 'Family' },
  { value: 'siblingInLaw', label: 'Sibling-in-Law', group: 'Family' },
  { value: 'childInLaw', label: 'Child-in-Law', group: 'Family' },
  // Professional
  { value: 'colleague', label: 'Colleague', group: 'Professional' },
  { value: 'boss', label: 'Boss', group: 'Professional' },
  { value: 'employee', label: 'Employee', group: 'Professional' },
  { value: 'mentor', label: 'Mentor', group: 'Professional' },
  { value: 'mentee', label: 'Mentee', group: 'Professional' },
  { value: 'classmate', label: 'Classmate', group: 'Professional' },
  { value: 'teacher', label: 'Teacher', group: 'Professional' },
  { value: 'student', label: 'Student', group: 'Professional' },
  // Other
  { value: 'neighbour', label: 'Neighbour', group: 'Other' },
  { value: 'acquaintance', label: 'Acquaintance', group: 'Other' },
  { value: 'other', label: 'Other', group: 'Other' },
];

export const INTENT_OPTIONS: { value: IntentType; label: string; icon: string }[] = [
  { value: 'catchUp', label: 'Catch Up', icon: 'fas fa-mug-hot' },
  { value: 'deepen', label: 'Deepen Connection', icon: 'fas fa-heart' },
  { value: 'maintain', label: 'Stay in Touch', icon: 'fas fa-hand-holding-heart' },
  { value: 'celebrate', label: 'Celebrate', icon: 'fas fa-champagne-glasses' },
  { value: 'support', label: 'Offer Support', icon: 'fas fa-hands-helping' },
  { value: 'collaborate', label: 'Collaborate', icon: 'fas fa-people-arrows' },
  { value: 'reconnect', label: 'Reconnect', icon: 'fas fa-rotate' },
  { value: 'apologise', label: 'Apologise', icon: 'fas fa-dove' },
  { value: 'thankYou', label: 'Say Thank You', icon: 'fas fa-gift' },
  { value: 'checkIn', label: 'Check In', icon: 'fas fa-phone' },
  { value: 'planEvent', label: 'Plan an Event', icon: 'fas fa-calendar-plus' },
  { value: 'shareNews', label: 'Share News', icon: 'fas fa-newspaper' },
  { value: 'askAdvice', label: 'Ask Advice', icon: 'fas fa-lightbulb' },
  { value: 'giveAdvice', label: 'Give Advice', icon: 'fas fa-comment-dots' },
  { value: 'introduce', label: 'Introduce Someone', icon: 'fas fa-user-plus' },
  { value: 'networkWith', label: 'Network', icon: 'fas fa-briefcase' },
  { value: 'justBecause', label: 'Just Because', icon: 'fas fa-smile' },
  { value: 'other', label: 'Other', icon: 'fas fa-ellipsis' },
];

export const FREQUENCY_OPTIONS: { value: FrequencyLevel; label: string; desc: string }[] = [
  { value: 'daily', label: 'Daily', desc: 'Every day' },
  { value: 'weekly', label: 'Weekly', desc: 'Once a week' },
  { value: 'fortnightly', label: 'Fortnightly', desc: 'Every two weeks' },
  { value: 'monthly', label: 'Monthly', desc: 'Once a month' },
  { value: 'quarterly', label: 'Quarterly', desc: 'Every three months' },
];

export const MEETING_CONTEXT_OPTIONS: { value: MeetingContext; label: string }[] = [
  { value: 'school', label: 'School' },
  { value: 'work', label: 'Work' },
  { value: 'online', label: 'Online' },
  { value: 'event', label: 'Event / Party' },
  { value: 'throughFriends', label: 'Through Friends' },
  { value: 'throughFamily', label: 'Through Family' },
  { value: 'neighbourhood', label: 'Neighbourhood' },
  { value: 'travel', label: 'Travel' },
  { value: 'dating', label: 'Dating' },
  { value: 'gym', label: 'Gym / Sports' },
  { value: 'hobby', label: 'Hobby / Interest Group' },
  { value: 'volunteering', label: 'Volunteering' },
  { value: 'professional', label: 'Professional Network' },
  { value: 'socialMedia', label: 'Social Media' },
  { value: 'randomEncounter', label: 'Random Encounter' },
  { value: 'other', label: 'Other' },
];

// ─── Core Types ─────────────────────────────────────────────────────────────

/** Person document at users/{uid}/people/{personId} */
export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  dateOfBirth?: string;         // YYYY-MM-DD
  profileImageUrl?: string;
  phone?: string;
  email?: string;
  /** Set when this address-book entry is linked to a pplkpr user account. */
  tetherHandle?: string;
  relationship: RelationshipType | '';
  meetingContext: MeetingContext | '';
  frequency: FrequencyLevel | '';
  intent: IntentType | '';
  notes?: string;
  isFavourite: boolean;
  createdAt: any;               // Firestore Timestamp
  updatedAt: any;               // Firestore Timestamp
  // Computed / aggregated
  lastContactDate?: any;
  nextReminderDate?: any;
  connectionScore?: number;      // 0-100 AI-computed
  /**
   * PPL-350 — does f8 hold a model of this person? PERMANENT once set: a plan
   * change never clears it, because f8 never forgets anyone.
   */
  remembered?: boolean;
  /**
   * When f8 stopped keeping up with them. Absent means active. This — not
   * `remembered` — is what the plan limit actually caps, so every gate reads
   * "remembered AND not paused".
   */
  pausedAt?: any;
}

/** Contact form data for add/edit wizard */
export interface ContactFormData {
  // Step 1: Basic Info
  firstName: string;
  lastName: string;
  nickname: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  // Step 2: Relationship
  relationship: RelationshipType | '';
  meetingContext: MeetingContext | '';
  notes: string;
  // Step 3: Engagement
  frequency: FrequencyLevel | '';
  intent: IntentType | '';
  isFavourite: boolean;
}

export const DEFAULT_CONTACT_FORM: ContactFormData = {
  firstName: '',
  lastName: '',
  nickname: '',
  dateOfBirth: '',
  phone: '',
  email: '',
  relationship: '',
  meetingContext: '',
  notes: '',
  frequency: '',
  intent: '',
  isFavourite: false,
};
